/**
 * SVG banner loader
 *
 * Template ใส่ <object type="image/svg+xml"> ชี้ URL ตรงไว้ใน markup อยู่แล้ว
 * banner จึงขึ้นตั้งแต่ HTML ชุดแรกโดยไม่ต้องรอ JS แต่ hero เป็น SVGator
 * animation (~2.5 MB) ถ้า stream ขาดกลางทาง browser จะ render กล่อง XML parse
 * error แทน banner สคริปต์นี้จึงทำหน้าที่ "อัปเกรด": fetch ไฟล์ให้ครบก่อน
 * แล้วค่อยสลับ data ของ <object> เดิมไปเป็น blob URL ถ้า fetch ไม่ได้
 * (เช่น bucket ยังไม่เปิด CORS) ก็ปล่อย <object> ที่ชี้ URL ตรงไว้เหมือนเดิม
 *
 * ข้อยกเว้นสำคัญ — WebKit: Safari/iOS และ webview ในแอป (Telegram, LINE)
 * ไม่โหลด blob: URL ใน <object> เลย sub-document ไม่เคยเกิด <object> เลยค้าง
 * ที่ขนาด default 300x150 และไม่มีภาพ = banner หายทั้งอัน (วัดจริงบน iOS 18.7:
 * URL ตรงได้ 440x367 ตามสัดส่วน viewBox / blob ได้ 440x150 เปล่า ๆ) จึงวัด
 * ความสามารถก่อนหนึ่งครั้งด้วย canUseBlobInObject() แล้วอัปเกรดเฉพาะที่รองรับ
 * ที่เหลือใช้ <object> ชี้ URL ตรงซึ่งทำงานได้ทุกเบราว์เซอร์ แลกกับการไม่มี
 * เกราะกัน XML parse error บน WebKit
 *
 * รองรับ 2 รูปแบบ:
 *  1) [data-svg-banner]                       — รูปเดียว
 *  2) [data-svg-banner-mobile] / [-desktop]   — responsive: เก็บเฉพาะ <object>
 *     ของ breakpoint ปัจจุบัน (ตัวที่ไม่ตรงถูกถอดออกเพื่อยกเลิกการโหลด) แล้ว
 *     สลับเมื่อ resize ข้าม 768px — ประหยัด bandwidth เพราะไม่ดึงทั้งสองไฟล์
 *     <object> ในแต่ละ breakpoint mark ด้วย [data-svg-banner-mode]
 */

const RETRY_DELAYS_MS = [1000, 3000];

const MOBILE_QUERY = '(max-width: 767px)';

/** SVG จิ๋วสำหรับวัดความสามารถ — ไม่ต้องเอาไฟล์จริง 1.4 MB มาลองผิดลองถูก */
const PROBE_SVG =
	'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 3"><rect width="4" height="3"/></svg>';

/** WebKit ไม่ยิง error event ให้ blob ที่โหลดไม่ได้ ต้องมี timeout เป็นคำตอบสุดท้าย */
const PROBE_TIMEOUT_MS = 2500;

const sleep = (ms: number): Promise<void> =>
	new Promise((resolve) => setTimeout(resolve, ms));

const createBannerObject = (data: string): HTMLObjectElement => {
	const el = document.createElement('object');
	el.setAttribute('type', 'image/svg+xml');
	el.setAttribute('data', data);
	el.setAttribute('aria-label', 'Banner');
	el.className = 'block w-full pointer-events-none';
	return el;
};

const fetchCompleteSvg = async (url: string): Promise<Blob> => {
	const response = await fetch(url);
	if (!response.ok) {
		throw new Error(`HTTP ${response.status}`);
	}
	const text = await response.text();
	const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
	if (doc.querySelector('parsererror')) {
		throw new Error('incomplete or invalid SVG');
	}
	return new Blob([text], { type: 'image/svg+xml' });
};

/**
 * blob: URL ใน <object> ใช้ได้จริงไหม — วัดครั้งเดียวแล้ว cache promise ไว้
 * เป็นการทดสอบความสามารถจริง ไม่ใช่ดม UA (webview ในแอปปลอม UA กันเป็นเรื่องปกติ)
 */
let blobObjectSupport: Promise<boolean> | null = null;

const canUseBlobInObject = (): Promise<boolean> => {
	if (blobObjectSupport) return blobObjectSupport;

	blobObjectSupport = new Promise<boolean>((resolve) => {
		const url = URL.createObjectURL(
			new Blob([PROBE_SVG], { type: 'image/svg+xml' })
		);
		const probe = document.createElement('object');
		probe.setAttribute('type', 'image/svg+xml');
		probe.setAttribute('data', url);
		probe.setAttribute('aria-hidden', 'true');
		probe.style.cssText =
			'position:absolute;left:-9999px;top:0;width:4px;height:3px;opacity:0';

		let settled = false;
		const finish = (ok: boolean): void => {
			if (settled) return;
			settled = true;
			clearTimeout(timer);
			probe.remove();
			URL.revokeObjectURL(url);
			resolve(ok);
		};

		const timer = setTimeout(() => finish(false), PROBE_TIMEOUT_MS);

		probe.addEventListener('load', () => {
			// blob: เป็น same-origin — ถ้าโหลดสำเร็จจริงต้องอ่าน contentDocument ได้
			try {
				finish(probe.contentDocument?.documentElement?.tagName === 'svg');
			} catch {
				finish(false);
			}
		});
		probe.addEventListener('error', () => finish(false));

		document.body.appendChild(probe);
	});

	return blobObjectSupport;
};

/** cache blob URL ต่อ source URL — สลับ breakpoint กลับไปมาแล้วไม่ต้อง fetch ซ้ำ */
const blobUrlCache = new Map<string, string>();

const resolveBannerSrc = async (url: string): Promise<string> => {
	const cached = blobUrlCache.get(url);
	if (cached) return cached;

	for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
		try {
			const blob = await fetchCompleteSvg(url);
			const blobUrl = URL.createObjectURL(blob);
			blobUrlCache.set(url, blobUrl);
			return blobUrl;
		} catch (err) {
			if (attempt < RETRY_DELAYS_MS.length) {
				await sleep(RETRY_DELAYS_MS[attempt]);
			} else {
				console.warn(
					`svg-banner: fetch failed, keeping direct <object>: ${url}`,
					err
				);
				return url;
			}
		}
	}

	return url;
};

/**
 * อัปเกรด <object> ที่ markup วางไว้แล้ว: สลับ data ไปเป็น blob URL
 * ถ้า resolve ไม่ได้ (คืน URL เดิม) ก็ไม่แตะ — ปล่อยตัวที่ชี้ URL ตรงทำงานต่อ
 */
const upgradeBannerObject = async (el: HTMLObjectElement): Promise<void> => {
	const url = el.getAttribute('data') ?? '';
	if (!url || url.startsWith('blob:')) return;

	// WebKit: อัปเกรดแล้วภาพหาย — ปล่อย <object> ที่ชี้ URL ตรงทำงานต่อดีกว่า
	if (!(await canUseBlobInObject())) return;

	const src = await resolveBannerSrc(url);
	// ระหว่าง fetch อาจถูกถอดออก (resize ข้าม breakpoint) หรือถูกสลับไปแล้ว
	if (src === url || !el.isConnected || el.getAttribute('data') !== url) {
		return;
	}

	// แก้ attribute data ของ <object> เดิมแล้วบาง browser ไม่ยอม reload —
	// สร้างตัวใหม่หน้าตาเหมือนเดิมแล้ว replace ทิ้งไปเลยชัวร์กว่า
	const upgraded = createBannerObject(src);
	upgraded.className = el.className;
	const mode = el.dataset.svgBannerMode;
	if (mode) upgraded.dataset.svgBannerMode = mode;
	el.replaceWith(upgraded);
};

/** ไม่มี <object> ใน markup (breakpoint ที่ถูกถอดไปแล้ว) — สร้างขึ้นใหม่ */
const renderBanner = async (
	placeholder: HTMLElement,
	url: string
): Promise<void> => {
	if (!url) return;
	const src = (await canUseBlobInObject()) ? await resolveBannerSrc(url) : url;
	placeholder.replaceChildren(createBannerObject(src));
};

/** รูปเดียว: [data-svg-banner] */
const initSingleBanner = (placeholder: HTMLElement): void => {
	const url = placeholder.dataset.svgBanner;
	if (!url) return;

	const existing = placeholder.querySelector<HTMLObjectElement>('object');
	if (existing) {
		void upgradeBannerObject(existing);
	} else {
		void renderBanner(placeholder, url);
	}
};

/** responsive: [data-svg-banner-mobile] / [data-svg-banner-desktop] */
const initResponsiveBanner = (placeholder: HTMLElement): void => {
	const mobileUrl = placeholder.dataset.svgBannerMobile ?? '';
	const desktopUrl = placeholder.dataset.svgBannerDesktop ?? '';

	const mql = window.matchMedia(MOBILE_QUERY);
	let currentMode = '';

	const apply = (): void => {
		const mode = mql.matches ? 'mobile' : 'desktop';
		// ถ้า breakpoint ยังไม่เปลี่ยน และมี <object> อยู่แล้ว ไม่ต้องทำซ้ำ
		if (mode === currentMode && placeholder.querySelector('object')) {
			return;
		}
		currentMode = mode;

		// ถอด <object> ของอีก breakpoint ออก — browser ยกเลิกการโหลดไฟล์ที่ไม่ใช้
		const kept = placeholder.querySelector<HTMLObjectElement>(
			`object[data-svg-banner-mode="${mode}"]`
		);
		placeholder.querySelectorAll<HTMLObjectElement>('object').forEach((el) => {
			if (el !== kept) el.remove();
		});

		if (kept) {
			void upgradeBannerObject(kept);
			return;
		}

		const url = mode === 'mobile' ? mobileUrl : desktopUrl;
		void renderBanner(placeholder, url || mobileUrl || desktopUrl);
	};

	apply();

	if (typeof mql.addEventListener === 'function') {
		mql.addEventListener('change', apply);
	} else {
		// Safari เก่า
		mql.addListener(apply);
	}
};

document.addEventListener('DOMContentLoaded', () => {
	document
		.querySelectorAll<HTMLElement>(
			'[data-svg-banner-mobile], [data-svg-banner-desktop]'
		)
		.forEach((el) => initResponsiveBanner(el));

	document
		.querySelectorAll<HTMLElement>('[data-svg-banner]')
		.forEach((el) => initSingleBanner(el));
});
